"use client";

import { useAppContext } from "@/context/AppContext";
import { useInternetOffers } from "@/hooks/useInternetOffers";
import {
  convertApiSpeedToMonarchUnit,
  normalizeMonarchSpeedForDisplay,
  speedsMatch,
} from "@/lib/normalize-speed-display";
import { PLAN_BUILDER_INTERNET_URL } from "@/lib/clickthrough-destination";
import type { Offer, OfferPricing } from "@/types/offers";
import { Suspense } from "react";
import ProductCardsLoader from "../ProductCardsLoader";
import { defaultPriceProductCardsData } from "./default";
import { PriceProductCards } from "./index";
import type {
  InitialToggleState,
  MobileFeaturedGridVariant,
  MobilePriceProductCardsDesignVariant,
  MonarchPriceProductCardConfig,
  PriceProductCardProps,
  PriceProductCardsDesignVariant,
  SectionPriceProductCardsEditableData,
} from "./types";

interface BasePriceProductCardsProps {
  data?: SectionPriceProductCardsEditableData;
}

function convertMonarchPricingToComponentFormat(
  basePrice: number,
  periodPrice?: number
) {
  return {
    oneYearPrice: basePrice,
    fiveYearPrice: periodPrice,
  };
}

function convertApiPricingToComponentFormat(
  apiPricing: OfferPricing,
  periodPrice?: number
) {
  const basePrice = apiPricing.price[0]?.monthlyCharges || 0;
  return {
    oneYearPrice: basePrice,
    fiveYearPrice: periodPrice,
  };
}

function mergeMonarchWithApiOffers(
  monarchCards: MonarchPriceProductCardConfig[],
  apiOffers: Offer[],
  designVariant?: PriceProductCardsDesignVariant
): Omit<PriceProductCardProps, "pricingView" | "sessionId">[] {
  return apiOffers.map((offer) => {
    const monarchConfig = monarchCards.find((monarchCard) => {
      const displayedMonarchSpeed = normalizeMonarchSpeedForDisplay(
        monarchCard.speed,
        monarchCard.unit
      );

      return speedsMatch(
        displayedMonarchSpeed.speed,
        displayedMonarchSpeed.unit,
        offer.speed,
        offer.unit
      );
    });

    let displaySpeed = offer.speed;
    let displayUnit = offer.unit;

    if (monarchConfig) {
      const converted = convertApiSpeedToMonarchUnit(
        offer.speed,
        offer.unit,
        monarchConfig.unit
      );
      displaySpeed = converted.speed;
      displayUnit = converted.unit;
    }

    return {
      offerId: offer.offerId,
      speed: displaySpeed,
      unit: displayUnit,
      featuresColor: monarchConfig?.featuresColor,
      mobileTopCard: monarchConfig?.mobileTopCard,
      topBanner: monarchConfig?.topBanner,
      pricing: convertApiPricingToComponentFormat(
        offer.pricing,
        monarchConfig?.periodPrice
      ),
      strikethroughPrice: monarchConfig?.strikethroughPrice,
      tagline:
        monarchConfig?.tagline || offer.badge || `${offer.speed} ${offer.unit}`,
      features: monarchConfig?.features?.length
        ? monarchConfig.features
        : offer.features || [],
      ctas: monarchConfig?.ctas || [
        {
          copy: "Shop",
          action: "link",
          url: PLAN_BUILDER_INTERNET_URL,
          display: "outline",
          tone: "neutral",
        },
      ],
      ctaBorderColor: monarchConfig?.ctaBorderColor,
      legalDisclaimer: monarchConfig?.legalDisclaimer || offer.eyebrow,
      badgeCopy: monarchConfig?.badgeCopy,
      mobileBadgeCopy: monarchConfig?.mobileBadgeCopy,
      mobileMostPopularTag: monarchConfig?.mobileMostPopularTag,
      mobileFeaturedGridCtaCopy: monarchConfig?.mobileFeaturedGridCtaCopy,
      mobileFeaturedGridIcon: monarchConfig?.mobileFeaturedGridIcon,
      mobileNoPricePostLegalCopy: monarchConfig?.mobileNoPricePostLegalCopy,
      mobileFeaturedGridBullets: monarchConfig?.mobileFeaturedGridBullets,
      elementName:
        monarchConfig?.elementName ||
        `PriceProductCard${offer.speed}${offer.unit}`,
      extendedTitle: monarchConfig?.extendedTitle,
      featuresIcon: monarchConfig?.featuresIcon,
      cardStyle: monarchConfig?.cardStyle,
      bundles: monarchConfig?.bundles,
      designVariant,
    };
  });
}

function convertMonarchCardsToStatic(
  monarchProducts: MonarchPriceProductCardConfig[],
  designVariant?: PriceProductCardsDesignVariant
): Omit<PriceProductCardProps, "pricingView" | "sessionId">[] {
  return monarchProducts.map((card) => {
    const normalized = normalizeMonarchSpeedForDisplay(card.speed, card.unit);

    return {
      speed: normalized.speed,
      unit: normalized.unit,
      featuresColor: card.featuresColor,
      mobileTopCard: card.mobileTopCard,
      topBanner: card.topBanner,
      pricing: convertMonarchPricingToComponentFormat(
        card.basePrice,
        card.periodPrice
      ),
      strikethroughPrice: card.strikethroughPrice,
      tagline: card.tagline,
      features: card.features,
      ctas: card.ctas || [
        {
          copy: "Check availability",
          action: "link",
          url: PLAN_BUILDER_INTERNET_URL,
          display: "outline",
          tone: "neutral",
        },
      ],
      ctaBorderColor: card.ctaBorderColor,
      legalDisclaimer: card.legalDisclaimer,
      badgeCopy: card.badgeCopy,
      mobileBadgeCopy: card.mobileBadgeCopy,
      mobileMostPopularTag: card.mobileMostPopularTag,
      mobileFeaturedGridCtaCopy: card.mobileFeaturedGridCtaCopy,
      mobileFeaturedGridIcon: card.mobileFeaturedGridIcon,
      mobileNoPricePostLegalCopy: card.mobileNoPricePostLegalCopy,
      mobileFeaturedGridBullets: card.mobileFeaturedGridBullets,
      elementName:
        card.elementName || `PriceProductCard${card.speed}${card.unit}`,
      extendedTitle: card.extendedTitle,
      featuresIcon: card.featuresIcon,
      cardStyle: card.cardStyle,
      bundles: card.bundles,
      designVariant,
    };
  });
}

function filterCardEntries<T>(cards: T[], cardsToDisplay?: string) {
  if (!cardsToDisplay || cardsToDisplay.trim() === "") {
    return cards.map((card, index) => ({
      card,
      originalIndex: index,
    }));
  }

  return cardsToDisplay
    .split(",")
    .reduce<Array<{ card: T; originalIndex: number }>>((acc, raw) => {
      const idx = parseInt(raw.trim(), 10);
      if (!isNaN(idx) && idx > 0 && idx <= cards.length) {
        const card = cards[idx - 1];
        if (card) {
          acc.push({
            card,
            originalIndex: idx - 1,
          });
        }
      }
      return acc;
    }, []);
}

function PriceProductCardsContent({
  data = defaultPriceProductCardsData,
}: BasePriceProductCardsProps) {
  const { localizationType, salesSessionId } = useAppContext();

  // Gate on the sales session id (SC cookie) rather than localizationType,
  // which also requires a sessionStorage address and is therefore unavailable
  // in a new tab even while the user is still localized.
  const shouldFetchOffers =
    data.isLocalizable !== false && Boolean(salesSessionId);

  const { offers, isLoading: offersLoading } = useInternetOffers(
    shouldFetchOffers ? salesSessionId : null
  );

  // Offers are keyed to the sales session, so returning any means the user is
  // shopping a localized catalog even when the tab has no stored address.
  const resolvedLocalizationType =
    offers && offers.length > 0 ? "full" : localizationType;

  const initialToggleState: InitialToggleState | undefined =
    data.initialToggleState ?? data.intitalToggleState;
  const mobileDesignVariant: MobilePriceProductCardsDesignVariant =
    data.mobileDesignVariant ?? "default";
  const mobileFeaturedGridVariant: MobileFeaturedGridVariant =
    data.mobileFeaturedGridVariant ?? "default";

  if (shouldFetchOffers && offersLoading) {
    return <ProductCardsLoader />;
  }

  if (shouldFetchOffers && offers && offers.length > 0) {
    const mergedCards = mergeMonarchWithApiOffers(
      data.products,
      offers,
      data.designVariant
    );
    const targetMonarchCard =
      typeof data.mobileInitialCard === "number" && data.mobileInitialCard > 0
        ? data.products?.[data.mobileInitialCard - 1]
        : undefined;
    const displayedTargetSpeed = targetMonarchCard
      ? normalizeMonarchSpeedForDisplay(
          targetMonarchCard.speed,
          targetMonarchCard.unit
        )
      : undefined;
    const resolvedMobileInitialSlide = displayedTargetSpeed
      ? offers.findIndex((offer) =>
          speedsMatch(
            displayedTargetSpeed.speed,
            displayedTargetSpeed.unit,
            offer.speed,
            offer.unit
          )
        )
      : -1;

    return (
      <PriceProductCards
        title={data.title}
        subtitle={data.subtitle}
        legalDisclaimer={data.legalDisclaimer}
        hasPeriodPricingToggle={data.hasPeriodPricingToggle}
        hasPriceSection={data.hasPriceSection}
        hasDesktopCarousel={data.hasDesktopCarousel}
        hasDesktopOverflowPeek={data.hasDesktopOverflowPeek}
        hasProductSelection={data.hasProductSelection}
        initialToggleState={initialToggleState}
        customPricingLabel={data.customPricingLabel}
        textAlign={data.textAlign}
        products={mergedCards}
        sessionId={salesSessionId || undefined}
        localizationType={resolvedLocalizationType}
        mobileInitialSlide={
          resolvedMobileInitialSlide >= 0
            ? resolvedMobileInitialSlide
            : undefined
        }
        mobileDesignVariant={mobileDesignVariant}
        mobileFeaturedGridVariant={mobileFeaturedGridVariant}
        designVariant={data.designVariant}
        elementName={data.elementName}
        ctas={data.ctas}
      />
    );
  }

  const staticCards = convertMonarchCardsToStatic(
    data.products,
    data.designVariant
  );
  const filteredCardEntries = filterCardEntries(
    staticCards,
    data.cardsToDisplay
  );
  const filteredCards = filteredCardEntries.map(({ card }) => card);
  const resolvedMobileInitialSlide = filteredCardEntries.findIndex(
    ({ originalIndex }) =>
      typeof data.mobileInitialCard === "number" &&
      data.mobileInitialCard > 0 &&
      originalIndex === data.mobileInitialCard - 1
  );

  return (
    <PriceProductCards
      title={data.title}
      subtitle={data.subtitle}
      legalDisclaimer={data.legalDisclaimer}
      hasPeriodPricingToggle={data.hasPeriodPricingToggle}
      hasPriceSection={data.hasPriceSection}
      hasDesktopCarousel={data.hasDesktopCarousel}
      hasDesktopOverflowPeek={data.hasDesktopOverflowPeek}
      hasProductSelection={data.hasProductSelection}
      initialToggleState={initialToggleState}
      customPricingLabel={data.customPricingLabel}
      textAlign={data.textAlign}
      products={filteredCards}
      localizationType={localizationType}
      mobileInitialSlide={
        resolvedMobileInitialSlide >= 0 ? resolvedMobileInitialSlide : undefined
      }
      mobileDesignVariant={mobileDesignVariant}
      mobileFeaturedGridVariant={mobileFeaturedGridVariant}
      designVariant={data.designVariant}
      elementName={data.elementName}
      ctas={data.ctas}
    />
  );
}

const BasePriceProductCards = ({ data }: BasePriceProductCardsProps) => {
  return (
    <Suspense fallback={<ProductCardsLoader />}>
      <PriceProductCardsContent data={data} />
    </Suspense>
  );
};

export default BasePriceProductCards;
